import type {
  ChecklistDto,
  ChecklistGroupDto,
  ChecklistItemDto,
} from '@iac/contracts';

export type StandardChecklist = Extract<ChecklistDto, { kind: 'standard' }>;

/**
 * What the pages derive from the one list response: a combined list carries
 * only its children's ids, and these resolve them.
 */

/**
 * Each group with its lists, both in the user's order — the list response is
 * already ordered by group and then by place in it.
 */
export function groupedLists(
  groups: readonly ChecklistGroupDto[],
  all: readonly ChecklistDto[],
): { group: ChecklistGroupDto; lists: ChecklistDto[] }[] {
  return groups.map((group) => ({
    group,
    lists: all.filter((l) => l.groupId === group.id),
  }));
}

/**
 * A combined list's children in the combined list's order; a standard list has
 * none. An id missing from `all` is skipped.
 */
export function childrenOf(
  list: ChecklistDto,
  all: readonly ChecklistDto[],
): StandardChecklist[] {
  if (list.kind === 'standard') return [];
  return list.childIds.flatMap((id) =>
    all.filter(
      (l): l is StandardChecklist => l.kind === 'standard' && l.id === id,
    ),
  );
}

/** The combined lists a standard list is a child of, A–Z. */
export function parentsOf(
  list: ChecklistDto,
  all: readonly ChecklistDto[],
  collator: Intl.Collator,
): ChecklistDto[] {
  return all
    .filter((l) => l.kind === 'combined' && l.childIds.includes(list.id))
    .sort((a, b) => collator.compare(a.name, b.name));
}

/** The lists whose items a reset of `listId` unticks: itself, or its children. */
export function affectedListIds(
  listId: string,
  all: readonly ChecklistDto[],
): Set<string> {
  const list = all.find((l) => l.id === listId);
  if (list?.kind === 'combined') return new Set(list.childIds);
  return new Set([listId]);
}

function itemsOf(
  list: ChecklistDto,
  all: readonly ChecklistDto[],
): ChecklistItemDto[] {
  if (list.kind === 'standard') return list.items;
  return all.flatMap((l) =>
    l.kind === 'standard' && list.childIds.includes(l.id) ? l.items : [],
  );
}

/** Ticked items over all items; a combined list counts its children's. */
export function progress(
  list: ChecklistDto,
  all: readonly ChecklistDto[],
): { done: number; total: number } {
  const items = itemsOf(list, all);
  return {
    done: items.filter((i) => i.checked).length,
    total: items.length,
  };
}
